/**
 * RightSidebarCopilot - HiveOS-styled Chat Panel
 *
 * A clean chat interface matching the HiveOS design with:
 * - Agent header with avatar and status
 * - Messages area with styled bubbles
 * - Voice input (record → transcribe → review → send)
 * - File uploads (select → preview → send with URL)
 * - Input at the bottom
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useCopilotChatInternal, useCopilotReadable } from '@copilotkit/react-core';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatActionProvider } from './a2ui/ChatActionContext';
import type { Components } from 'react-markdown';
import * as Icons from '../icons/Icons';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import { useTranscription } from '../../hooks/useTranscription';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useA2UIActions } from './a2ui/useA2UIActions';

type InteractionMode = 'a2ui' | 'navigation';

interface Recommendation {
  key: string;
  priority: string;
  description: string;
}

interface RightSidebarCopilotProps {
  isCollapsed?: boolean;
  agentId?: string;
  agentName?: string;
  agentAvatar?: string;
  onClose?: () => void;
  autoGreet?: boolean;
  onAutoGreetSent?: () => void;
  completedSteps?: string[];
  recommendations?: Recommendation[];
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onboardingMode?: boolean;
  currentStep?: string | null;
}

interface RightSidebarCopilotInnerProps {
  agentId?: string;
  agentName?: string;
  agentAvatar?: string;
  onClose?: () => void;
  autoGreet?: boolean;
  onAutoGreetSent?: () => void;
  completedSteps?: string[];
  recommendations?: Recommendation[];
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onboardingMode?: boolean;
  currentStep?: string | null;
}

// Custom code block component with copy button (ChatGPT-style)
const CodeBlock = ({ node, inline, className, children, ...props }: any) => {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  const code = String(children).replace(/\n$/, '');

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (inline) {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  }

  return (
    <div className="relative group my-4">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-800 dark:bg-slate-900 rounded-t-lg border-b border-slate-700">
        <span className="text-xs text-slate-400 font-mono">{language || 'code'}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-2 py-1 text-xs text-slate-400 hover:text-white transition-colors rounded"
        >
          {copied ? (
            <>
              <Icons.CheckIcon className="w-3 h-3" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Icons.CopyIcon className="w-3 h-3" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className="m-0 rounded-t-none overflow-x-auto">
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    </div>
  );
};

/** Format file size for display */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Format recording duration as mm:ss */
function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Outer wrapper - no hooks that establish connections.
// When isCollapsed=true, the inner component is not mounted,
// so CopilotKit doesn't establish an AG-UI connection.
const RightSidebarCopilot: React.FC<RightSidebarCopilotProps> = React.memo(({
  isCollapsed = false,
  agentId,
  agentName,
  agentAvatar,
  onClose,
  autoGreet,
  onAutoGreetSent,
  completedSteps,
  recommendations,
  isExpanded,
  onToggleExpand,
  onboardingMode,
  currentStep,
}) => {
  if (isCollapsed) return null;
  return (
    <RightSidebarCopilotInner
      agentId={agentId}
      agentName={agentName}
      agentAvatar={agentAvatar}
      onClose={onClose}
      autoGreet={autoGreet}
      onAutoGreetSent={onAutoGreetSent}
      completedSteps={completedSteps}
      recommendations={recommendations}
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
      onboardingMode={onboardingMode}
      currentStep={currentStep}
    />
  );
});

// Inner component - contains CopilotKit hook and all chat UI.
// Only mounted when chat panel is visible.
const RightSidebarCopilotInner: React.FC<RightSidebarCopilotInnerProps> = ({
  agentId = 'meta_assistant',
  agentName: agentNameProp,
  agentAvatar,
  onClose,
  autoGreet,
  onAutoGreetSent,
  completedSteps,
  recommendations,
  isExpanded,
  onToggleExpand,
  onboardingMode,
  currentStep,
}) => {
  const { t, locale } = useTranslation();
  const agentName = agentNameProp || t('chat.assistantName', 'Assistant SmartBeez');
  const { user } = useAuth();
  const location = useLocation();

  // A2UI / Navigation mode toggle — persisted in localStorage
  const [interactionMode, setInteractionMode] = useState<InteractionMode>(
    () => (localStorage.getItem('smartbeez_interaction_mode') as InteractionMode) || 'a2ui'
  );
  const toggleMode = useCallback(() => {
    setInteractionMode((prev) => {
      const next = prev === 'a2ui' ? 'navigation' : 'a2ui';
      localStorage.setItem('smartbeez_interaction_mode', next);
      return next;
    });
  }, []);

  // Mutation cards preference — controls inline A2UI cards after mutations
  type MutationCardsPref = 'always' | 'onboarding_only' | 'never';
  const [mutationCards, setMutationCards] = useState<MutationCardsPref>(
    () => (localStorage.getItem('smartbeez_mutation_cards') as MutationCardsPref) || 'onboarding_only'
  );
  const cycleMutationCards = useCallback(() => {
    setMutationCards((prev) => {
      const order: MutationCardsPref[] = ['onboarding_only', 'always', 'never'];
      const next = order[(order.indexOf(prev) + 1) % order.length];
      localStorage.setItem('smartbeez_mutation_cards', next);
      return next;
    });
  }, []);

  // Register CopilotKit A2UI actions (inline components + navigation)
  useA2UIActions({ mode: interactionMode });

  // Input state
  const [inputValue, setInputValue] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevLoadingRef = useRef(false);
  // Stable timestamps per message (AG-UI Messages have no createdAt).
  // Each message gets a Date assigned when first seen.
  const messageTimesRef = useRef<Map<string, Date>>(new Map());

  // CopilotKit chat hook
  const {
    messages,
    sendMessage,
    isLoading,
  } = useCopilotChatInternal();

  // Bridge sendMessage to A2UI render components via context.
  // CopilotKit's sendMessage is loosely typed (see @copilotkit/react-core
  // bundled types) — we build the minimum viable message shape it expects.
  type CopilotUserMessage = { id: string; role: 'user'; content: string };
  const chatActionValue = useMemo(() => ({
    sendChatMessage: (text: string) => {
      const message: CopilotUserMessage = {
        id: `card-${Date.now()}`,
        role: 'user',
        content: text,
      };
      (sendMessage as (m: CopilotUserMessage) => unknown)(message);
    },
  }), [sendMessage]);

  // Voice input hooks
  const {
    isRecording,
    isSupported: micSupported,
    duration: recordingDuration,
    error: recorderError,
    startRecording,
    stopRecording,
  } = useAudioRecorder();

  const {
    transcribe,
    isTranscribing,
    error: transcriptionError,
  } = useTranscription();

  // File upload hook
  const {
    selectFile,
    pendingFile,
    clearPendingFile,
    uploadFile,
    isUploading,
    error: uploadError,
  } = useFileUpload();

  // Expose console context to CopilotKit so the MetaAgent knows
  // what page the operator is on, which agent they're viewing,
  // and which interaction mode they prefer (a2ui or navigation)
  useCopilotReadable({
    description: 'Current console context',
    value: {
      currentPage: location.pathname,
      currentAgent: { id: agentId, name: agentName },
      interactionMode,
      mutationCards,
      locale,
      onboardingMode: onboardingMode ?? false,
      currentStep: currentStep ?? null,
    },
  });

  // Record stable timestamp for each new message when first seen.
  useEffect(() => {
    const times = messageTimesRef.current;
    for (const msg of messages) {
      if (!times.has(msg.id)) {
        times.set(msg.id, new Date());
      }
    }
  }, [messages]);

  const getMessageTime = useCallback((id: string): Date => {
    return messageTimesRef.current.get(id) ?? new Date();
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Determine if any async operation is in progress
  const isBusy = isLoading || isTranscribing || isUploading;
  const canSend = (inputValue.trim() || pendingFile) && !isBusy;

  // Send message handler — includes file attachment if pending
  const handleSend = useCallback(async () => {
    const hasText = inputValue.trim().length > 0;
    const hasFile = !!pendingFile;
    if ((!hasText && !hasFile) || isBusy) return;

    let content = inputValue.trim();

    // Upload file and prepend as markdown link
    if (hasFile) {
      const result = await uploadFile(pendingFile.file);
      if (result) {
        const fileLink = `[${result.name}](${result.url})`;
        content = content ? `${fileLink}\n\n${content}` : fileLink;
        clearPendingFile();
      } else {
        // Upload failed — keep pending file so user can retry
        return;
      }
    }

    if (!content) return;

    await sendMessage({
      id: Date.now().toString(),
      role: 'user',
      content,
    });

    setInputValue('');
  }, [inputValue, isBusy, pendingFile, sendMessage, uploadFile, clearPendingFile]);

  // Voice recording handlers
  const handleMicClick = useCallback(async () => {
    if (isRecording) {
      // Stop recording → transcribe → put text in input
      const blob = await stopRecording();
      if (blob) {
        const result = await transcribe(blob, locale);
        if (result?.text) {
          setInputValue((prev) => (prev ? `${prev} ${result.text}` : result.text));
          // Focus input so user can review/edit before sending
          requestAnimationFrame(() => inputRef.current?.focus());
        }
      }
    } else {
      await startRecording();
    }
  }, [isRecording, stopRecording, startRecording, transcribe]);

  // Auto-focus input after response is received
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(() => {
          if (inputRef.current && !inputRef.current.disabled) {
            inputRef.current.focus();
          }
        });
      }, 200);
      return () => clearTimeout(timeoutId);
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading]);

  // Auto-greeting: when sidebar opens via ?guide=1, send a trigger message
  // so ASA starts the onboarding flow without waiting for user input.
  // Uses recommendations (Task 054) or falls back to completedSteps.
  // Auto-greeting: send a trigger message so ASA starts the onboarding flow.
  // IMPORTANT: Set the ref inside the timer callback, NOT when the timer starts.
  // React 18 StrictMode double-mounts: mount → cleanup (clears timer) → remount.
  // If the ref is set eagerly on mount, the remount sees it as true and skips.
  const autoGreetFiredRef = useRef(false);
  useEffect(() => {
    if (autoGreet && !autoGreetFiredRef.current && !isLoading && messages.length === 0) {
      // Small delay to let CopilotKit establish its connection
      const timer = setTimeout(() => {
        if (autoGreetFiredRef.current) return; // Guard against race
        autoGreetFiredRef.current = true;

        let greetContent: string;

        // Prefer recommendation-based greeting (Task 054 Phase 3)
        // All paths are locale-aware so ASA responds in the user's language.
        const requiredRecs = recommendations?.filter((r) => r.priority === 'required') ?? [];
        if (completedSteps && completedSteps.length > 0) {
          const steps = completedSteps.join(', ');
          greetContent = locale === 'fr'
            ? `Je continue ma configuration. Étapes déjà complétées : ${steps}. Quelle est la prochaine étape ?`
            : locale === 'es'
            ? `Continúo mi configuración. Pasos completados: ${steps}. ¿Qué sigue?`
            : `I'm continuing my onboarding setup. Steps already completed: ${steps}. What should I do next?`;
        } else if (recommendations && recommendations.length > 0 && requiredRecs.length > 0) {
          greetContent = locale === 'fr'
            ? 'Bonjour ! Je viens de créer mon organisation et je voudrais commencer la configuration.'
            : locale === 'es'
            ? '¡Hola! Acabo de crear mi organización y me gustaría empezar la configuración.'
            : 'Hello! I just created my organization and I\'d like to get started.';
        } else {
          greetContent = locale === 'fr'
            ? 'Bonjour ! Je viens de créer mon organisation.'
            : locale === 'es'
            ? '¡Hola! Acabo de crear mi organización.'
            : 'Hello! I just created my organization.';
        }

        sendMessage({
          id: `auto-greet-${Date.now()}`,
          role: 'user',
          content: greetContent,
        });
        onAutoGreetSent?.();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [autoGreet, isLoading, messages.length, sendMessage, onAutoGreetSent, completedSteps, recommendations, locale]);

  const activeError = recorderError || transcriptionError || uploadError;

  return (
    <ChatActionProvider value={chatActionValue}>
    <div className="h-full flex flex-col bg-background-panel relative overflow-hidden">
      {/* Main Chat Card */}
      <div className="flex-1 flex flex-col m-2 md:m-3 bg-background-inner rounded-[1.5rem] border border-black/5 dark:border-white/5 overflow-hidden shadow-xl relative">

        {/* Agent Header */}
        <div className="p-4 flex items-center justify-between border-b border-black/[0.05] dark:border-white/[0.05] bg-background-panel/30 backdrop-blur-sm">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-background-panel overflow-hidden border border-black/5 shadow-md shrink-0">
              {agentAvatar ? (
                <img src={agentAvatar} alt={agentName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-amber-500 flex items-center justify-center">
                  <span className="text-xl">🐝</span>
                </div>
              )}
            </div>
            <div className="min-w-0">
              <h4 className="text-sm font-bold truncate text-slate-900 dark:text-white">{agentName}</h4>
              <p className="text-[9px] text-blue-500 font-black tracking-widest uppercase flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                {t('common.liveConnection') || 'LIVE CONNECTION'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* A2UI / Navigation mode toggle */}
            <button
              onClick={toggleMode}
              title={interactionMode === 'a2ui' ? 'Switch to Navigation mode' : 'Switch to A2UI mode'}
              className="flex items-center gap-0.5 px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider rounded-lg border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            >
              <span className={interactionMode === 'a2ui' ? 'text-blue-500' : 'text-slate-400 dark:text-slate-500'}>
                A2UI
              </span>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <span className={interactionMode === 'navigation' ? 'text-blue-500' : 'text-slate-400 dark:text-slate-500'}>
                Nav
              </span>
            </button>
            {/* Mutation cards detail level toggle */}
            <button
              onClick={cycleMutationCards}
              title={
                mutationCards === 'always' ? t('a2ui.mutationCards.always', 'Detail cards: Always')
                : mutationCards === 'never' ? t('a2ui.mutationCards.never', 'Detail cards: Never')
                : t('a2ui.mutationCards.onboarding_only', 'Detail cards: Onboarding')
              }
              className="flex items-center gap-0.5 px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider rounded-lg border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            >
              <span className={mutationCards === 'always' ? 'text-blue-500' : mutationCards === 'never' ? 'text-slate-400 dark:text-slate-500' : 'text-amber-500'}>
                {mutationCards === 'always' ? 'Cards' : mutationCards === 'never' ? 'Off' : 'Onb'}
              </span>
            </button>
            <button className="p-2 text-slate-500 dark:text-white hover:text-slate-700 dark:hover:text-white transition-colors">
              <Icons.Search className="w-4 h-4" />
            </button>
            {onToggleExpand && (
              <button
                onClick={onToggleExpand}
                className="hidden md:flex p-2 text-slate-500 dark:text-white hover:text-slate-700 dark:hover:text-white transition-colors"
                title={isExpanded ? 'Shrink panel' : 'Expand panel'}
              >
                {isExpanded ? (
                  <Icons.ShrinkIcon className="w-4 h-4" />
                ) : (
                  <Icons.ExpandIcon className="w-4 h-4" />
                )}
              </button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 bg-black/5 dark:bg-white/5 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
              >
                <Icons.ChevronDown className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-6 space-y-4 custom-scrollbar"
        >
          {/* Initial greeting if no messages */}
          {messages.length === 0 && !autoGreet && (
            <div className="flex flex-col items-start animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="max-w-[85%] rounded-2xl rounded-tl-none px-4 py-3 text-sm leading-relaxed shadow-sm bg-white/5 dark:bg-slate-800/50 text-slate-900 dark:text-white border border-black/5 dark:border-white/5">
                {t('chat.welcomeGreeting', `Bonjour ! Je suis ${agentName}, votre assistant IA. Comment puis-je vous aider ?`, { agentName })}
              </div>
            </div>
          )}

          {/* Messages — skip empty bubbles and tool handler returns */}
          {messages.filter((msg) => {
            if (typeof msg.content === 'string' && msg.content.trim() === '') return false;
            if (msg.content == null) return false;
            // Hide raw JSON tool handler returns (e.g. {"displayed":true}, {"success":true})
            // Also hide structured ToolMutationResult returns (rendered as A2UI cards)
            if (typeof msg.content === 'string') {
              const trimmed = msg.content.trim();
              if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
                // Short handler returns (< 100 chars, likely {"displayed":true})
                if (trimmed.length < 100) return false;
                // Structured mutation results: must start with {"success": and contain entity_type
                if (trimmed.startsWith('{"success"') && trimmed.includes('"entity_type"')) return false;
              }
            }
            return true;
          }).map((msg) => (
            <div
              key={msg.id}
              className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
            >
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white font-medium rounded-tr-none'
                  : 'bg-white/5 dark:bg-slate-800/50 text-slate-900 dark:text-white border border-black/5 dark:border-white/5 rounded-tl-none'
              }`}>
                {typeof msg.content === 'string' ? (
                  <div className={`prose prose-sm max-w-none ${
                    msg.role === 'user'
                      ? 'prose-headings:text-white prose-p:text-white prose-strong:text-white prose-em:text-white/90 prose-code:text-white prose-li:text-white prose-a:text-blue-200'
                      : 'prose-headings:text-slate-900 dark:prose-headings:text-white prose-p:text-slate-800 dark:prose-p:text-slate-200 prose-strong:text-slate-900 dark:prose-strong:text-white prose-code:bg-slate-100 dark:prose-code:bg-slate-800 prose-code:text-slate-900 dark:prose-code:text-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-slate-900 dark:prose-pre:bg-slate-950 prose-li:text-slate-800 dark:prose-li:text-slate-200 prose-a:text-blue-600 dark:prose-a:text-blue-400'
                  } prose-headings:font-semibold prose-headings:my-2 prose-p:my-1.5 prose-p:leading-relaxed prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-pre:my-2 prose-pre:p-3 prose-blockquote:my-2 prose-blockquote:border-l-slate-300 dark:prose-blockquote:border-l-slate-600 prose-blockquote:italic`}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code: CodeBlock
                      } as Components}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  JSON.stringify(msg.content)
                )}
              </div>
              <span className="text-[9px] text-slate-400 dark:text-slate-500 mt-1.5 px-2 font-bold uppercase">
                {getMessageTime(msg.id).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))}

          {/* Typing indicator */}
          {isLoading && (
            <div className="flex gap-1.5 px-4 items-center">
              <div className="w-1.5 h-1.5 bg-blue-500/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 bg-blue-500/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 bg-blue-500/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="p-3 bg-background-inner shrink-0 border-t border-black/5 dark:border-white/5">

          {/* Recording indicator bar */}
          {isRecording && (
            <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-medium text-red-600 dark:text-red-400">
                Recording {formatDuration(recordingDuration)}
              </span>
              <button
                onClick={handleMicClick}
                className="ml-auto text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
              >
                Stop
              </button>
            </div>
          )}

          {/* Transcribing indicator */}
          {isTranscribing && (
            <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40">
              <div className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
              <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                Transcribing audio...
              </span>
            </div>
          )}

          {/* File preview bar */}
          {pendingFile && (
            <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              {pendingFile.preview ? (
                <img
                  src={pendingFile.preview}
                  alt={pendingFile.file.name}
                  className="w-8 h-8 rounded object-cover"
                />
              ) : (
                <Icons.PaperclipIcon className="w-4 h-4 text-slate-400" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">
                  {pendingFile.file.name}
                </p>
                <p className="text-[10px] text-slate-400">
                  {formatFileSize(pendingFile.file.size)}
                </p>
              </div>
              <button
                onClick={clearPendingFile}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <Icons.XIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Error bar */}
          {activeError && (
            <div className="px-3 py-1.5 mb-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-xs text-red-600 dark:text-red-400">
              {activeError}
            </div>
          )}

          {/* Input row */}
          <div className="relative flex items-center bg-background-panel rounded-2xl border border-black/10 dark:border-white/10 px-2 py-2 gap-1 shadow-inner focus-within:ring-2 focus-within:ring-blue-500/20 transition-all">
            {/* Paperclip / file attach */}
            <button
              onClick={selectFile}
              disabled={isBusy}
              className="p-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400 transition-colors disabled:opacity-40"
              type="button"
              title="Attach file"
            >
              <Icons.PaperclipIcon className="w-4 h-4" />
            </button>

            {/* Text input */}
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={isRecording ? 'Recording...' : 'Message your agent...'}
              disabled={isBusy}
              className="flex-1 bg-transparent border-none focus:ring-0 focus:outline-none text-sm py-1.5 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-400"
            />

            {/* Mic button */}
            {micSupported && (
              <button
                onClick={handleMicClick}
                disabled={isTranscribing || isLoading || isUploading}
                className={`p-2 transition-colors disabled:opacity-40 ${
                  isRecording
                    ? 'text-red-500 hover:text-red-600 animate-pulse'
                    : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400'
                }`}
                type="button"
                title={isRecording ? 'Stop recording' : 'Voice input'}
              >
                <Icons.MicIcon className="w-4 h-4" />
              </button>
            )}

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={!canSend}
              className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all ${
                canSend
                  ? 'bg-blue-600 text-white shadow-lg hover:bg-blue-700'
                  : 'text-slate-400 dark:text-slate-500'
              }`}
            >
              {isBusy ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Icons.SendIcon className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
    </ChatActionProvider>
  );
};

RightSidebarCopilot.displayName = 'RightSidebarCopilot';

export default RightSidebarCopilot;
